// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Cargo Tab Component
 * 
 * Displays ship resources (food, water, oxygen, medical, parts) with consumption rates
 * and trade goods inventory
 */

import React, { useState, useEffect } from 'react';
import ResourceManager from '../../utils/game/ResourceManager';
import CrewManager from '../../utils/game/CrewManager';
import { useCargo } from '../../contexts/CargoContext';
import { useModules } from '../../contexts/ModulesContext';
import tradeGoods from '../../config/TradeGoodsConfig';
import './CargoTab.css';

const CargoTab = () => {
  const { getCargoQuantity, getCargoUsed } = useCargo();
  const { getCargoCapacity } = useModules();
  const [resources, setResources] = useState({});
  const [crewCount, setCrewCount] = useState(10);
  // eslint-disable-next-line no-unused-vars
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Update resources every second
  useEffect(() => {
    const updateResources = () => {
      setResources(ResourceManager.getAllResources());
      setCrewCount(CrewManager.getCrewCount());
      setRefreshTrigger(prev => prev + 1);
    };

    updateResources();
    const interval = setInterval(updateResources, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get resource display data
  const getResourceData = (resourceType) => {
    const current = resources[resourceType] || 0;
    const max = ResourceManager.maxCapacity[resourceType];
    const percentage = ResourceManager.getResourcePercentage(resourceType);
    const status = ResourceManager.getResourceStatus(resourceType, crewCount);
    const color = ResourceManager.getStatusColor(status);
    const daysRemaining = ResourceManager.getDaysRemaining(resourceType, crewCount);
    const dailyConsumption = ResourceManager.getDailyConsumption(resourceType, crewCount);

    return {
      current: Math.floor(current),
      max,
      percentage,
      status,
      color,
      daysRemaining: isFinite(daysRemaining) ? Math.floor(daysRemaining) : '∞',
      dailyConsumption: dailyConsumption.toFixed(1)
    };
  };

  // Resource icons
  const resourceIcons = {
    food: '🍎',
    water: '💧',
    oxygen: '💨',
    medical: '🏥',
    parts: '🔧'
  };

  // Resource names
  const resourceNames = {
    food: 'Food',
    water: 'Water',
    oxygen: 'Oxygen',
    medical: 'Medical',
    parts: 'Parts'
  };

  return (
    <div className="cargo-tab">
      {/* Resources Section */}
      <div className="cargo-section">
        <div className="cargo-section-title">SHIP RESOURCES</div>
        
        <div className="resources-grid">
          {Object.keys(resourceIcons).map(resourceType => {
            const data = getResourceData(resourceType);
            
            return (
              <div key={resourceType} className="resource-item">
                <div className="resource-header">
                  <span className="resource-icon">{resourceIcons[resourceType]}</span>
                  <span className="resource-name">{resourceNames[resourceType]}</span>
                  {data.status !== 'ok' && (
                    <span className="resource-warning" style={{ color: data.color }}>
                      {data.status === 'empty' ? '⚠️ EMPTY' : 
                       data.status === 'critical' ? '⚠️ CRITICAL' : 
                       '⚠️ LOW'}
                    </span>
                  )}
                </div>
                
                <div className="resource-bar-container">
                  <div className="resource-bar">
                    <div 
                      className="resource-bar-fill" 
                      style={{ 
                        width: `${data.percentage}%`,
                        background: data.color,
                        boxShadow: `0 0 10px ${data.color}`
                      }}
                    />
                  </div>
                  <div className="resource-amount">
                    {data.current}/{data.max}
                  </div>
                </div>
                
                <div className="resource-info">
                  <div className="resource-stat">
                    <span className="stat-label">Days Left:</span>
                    <span className="stat-value" style={{ color: data.color }}>
                      {data.daysRemaining}
                    </span>
                  </div>
                  <div className="resource-stat">
                    <span className="stat-label">Per Day:</span>
                    <span className="stat-value">-{data.dailyConsumption}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rationing Status */}
        {ResourceManager.isRationing() && (
          <div className="rationing-notice">
            ⚠️ RATIONING MODE ACTIVE - 50% consumption
          </div>
        )}
      </div>

      {/* Trade Goods Section */}
      <div className="cargo-section">
        <div className="cargo-section-title">TRADE GOODS</div>
        {Object.values(tradeGoods).some(good => getCargoQuantity(good.id) > 0) ? (
          <div className="trade-goods-list">
            {Object.values(tradeGoods).map(good => {
              const quantity = getCargoQuantity(good.id);
              if (quantity === 0) return null;
              return (
                <div key={good.id} className="trade-good-item">
                  <span className="trade-good-icon">{good.icon}</span>
                  <span className="trade-good-name">{good.name}</span>
                  <span className="trade-good-quantity">{quantity}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <div className="empty-text">No trade goods in cargo</div>
            <div className="empty-hint">Visit a trading post to buy goods</div>
          </div>
        )}
      </div>

      {/* Cargo Capacity */}
      <div className="cargo-footer">
        <div className="cargo-capacity">
          <span className="capacity-label">CARGO CAPACITY:</span>
          <span className="capacity-value">{getCargoUsed(tradeGoods)}/{getCargoCapacity()} tons</span>
        </div>
      </div>
    </div>
  );
};

export default CargoTab;
